import { Search, Tabs } from "@procore/core-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin, Events, track } from "apis/procore";
import { SlottedWindow } from "components/window/SlottedWindow";
import { CORE_PROPERTIES_TAB_IDS, usePropertiesTabs } from "contexts/propertiesTabs";
import type { BaseWindowProps } from "contexts/windows/reducer";
import { useDbStatus } from "hooks/useDbStatus";
import { useObjectData } from "hooks/useObjectData";
import { decodeObjPathName } from "utils/objectData";

import { useBulkActionsContextMenu } from "../hooks/useBulkActionsContextMenu";

export function PropertiesWindow({ onClose, position }: BaseWindowProps) {
    const { t } = useTranslation();
    const { object, hasAccess, isLoading } = useObjectData();
    const dbStatus = useDbStatus();
    const customTabs = usePropertiesTabs();
    const [searchTerm, setSearchTerm] = useState("");

    const { openContextMenu, contextMenuNode } = useBulkActionsContextMenu({
        origin: EventOrigin.PropertiesPanel,
    });

    const isEditableContextMenuTarget = (target: EventTarget | null) => {
        if (!(target instanceof Element)) {
            return false;
        }
        return Boolean(target.closest("input, textarea, [contenteditable='true'], [contenteditable='']"));
    };

    const handleContextMenu = (event: React.MouseEvent) => {
        if (isEditableContextMenuTarget(event.target)) {
            return;
        }
        event.preventDefault();
        openContextMenu(event.clientX, event.clientY);
    };

    const [activeTab, setActiveTab] = useState<string>(() => {
        return Object.keys(customTabs.tabEntries)[0] || CORE_PROPERTIES_TAB_IDS.PROPERTIES;
    });

    const [searchEmptyState, setSearchEmptyState] = useState(false);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        const trimmed = searchTerm.trim();
        if (!trimmed) {
            return;
        }
        searchDebounceRef.current = setTimeout(() => {
            void track(Events.propertyPanel.searched, { search_term: trimmed });
        }, 500);
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = null;
            }
        };
    }, [searchTerm]);

    const isSearchActive = searchTerm.trim().length > 0;
    const error = object && !hasAccess ? t("objectAccessForbidden") : null;
    const rawObjectName = object?.base.find(([key]) => key === "Name")?.[1];
    const objectName = rawObjectName ? decodeObjPathName(rawObjectName) : object ? `Object ${object.id}` : "";

    const getEmptyState = () => {
        if (!object) {
            if (!dbStatus.isDataAvailable) {
                return dbStatus.unavailableReason === "offline"
                    ? {
                          title: t("propertiesUnavailableOffline"),
                          message: t("propertiesUnavailableOfflineMessage"),
                          showIcon: false,
                      }
                    : {
                          title: t("databasePreparing"),
                          message: t("propertiesUnavailablePreparingMessage"),
                          showIcon: false,
                      };
            }
            return {
                title: t("selectAnObject"),
                message: t("selectObjectForProperties"),
            };
        }
        if (activeTab === CORE_PROPERTIES_TAB_IDS.PROPERTIES && isSearchActive && searchEmptyState) {
            return {
                title: t("noPropertiesMatchSearch"),
                message: t("pleaseRetrySearch"),
            };
        }
        return undefined;
    };

    const tabItems = useMemo(
        () =>
            Object.values(customTabs.tabEntries).map((tabEntry) => (
                <Tabs.Tab
                    key={tabEntry.config.id}
                    selected={activeTab === tabEntry.config.id}
                    onClick={() => setActiveTab(tabEntry.config.id)}
                    data-testid={`properties-tab-${tabEntry.config.id}`}
                >
                    {tabEntry.config.id === CORE_PROPERTIES_TAB_IDS.PROPERTIES
                        ? t("properties")
                        : tabEntry.config.label}
                </Tabs.Tab>
            )),
        [customTabs.tabEntries, activeTab, t],
    );

    const renderActiveTab = () => {
        const tabEntry = customTabs.tabEntries[activeTab];
        if (!tabEntry) {
            return <div data-testid="properties-tab-not-found">Tab not found</div>;
        }
        const TabComponent = tabEntry.config.component;
        return (
            <div data-testid={`properties-tab-content-${activeTab}`}>
                <TabComponent
                    object={object}
                    hasAccess={hasAccess}
                    searchTerm={searchTerm}
                    isSearchActive={isSearchActive}
                    onEmptyStateChange={setSearchEmptyState}
                />
            </div>
        );
    };

    return (
        <SlottedWindow
            position={position}
            size={{ width: 400, height: 500 }}
            loading={isLoading}
            loadingMessage={t("loadingProperties")}
            error={error}
            emptyState={getEmptyState()}
            onClose={onClose}
            secondaryBgColor
            headerRegion={
                <SlottedWindow.Title subtitle={object?.fileName}>
                    {objectName.length > 0 ? objectName : t("properties")}
                </SlottedWindow.Title>
            }
            subheaderRegion={
                object && (
                    <>
                        <SlottedWindow.TabsRow>
                            <Tabs data-testid="properties-tabs">{tabItems}</Tabs>
                        </SlottedWindow.TabsRow>
                        {activeTab === CORE_PROPERTIES_TAB_IDS.PROPERTIES && (
                            <SlottedWindow.SubheaderRow>
                                <Search
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t("searchProperties")}
                                    autoComplete="off"
                                    data-testid="properties-search"
                                />
                            </SlottedWindow.SubheaderRow>
                        )}
                    </>
                )
            }
            data-testid="properties-window"
        >
            <div onContextMenu={handleContextMenu}>{renderActiveTab()}</div>
            {contextMenuNode}
        </SlottedWindow>
    );
}
